import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, Box } from "lucide-react";
import { useRooms } from "@/hooks/useRooms";
import { toast } from "sonner";
import { useTranslation } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: File[];
  onUploadFiles: (files: File[], roomId: string | null) => Promise<void>;
}

export const MultiUploadRouteDialog: React.FC<Props> = ({ open, onOpenChange, files, onUploadFiles }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rooms, createRoom } = useRooms();
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleNewRoom = async () => {
    setBusyId("__new__");
    try {
      const room = await createRoom();
      if (!room) throw new Error("create failed");
      await onUploadFiles(files, room.id);
      onOpenChange(false);
      navigate(`/room/${room.id}`);
    } catch (e) {
      console.error(e);
      toast.error(t("dashboard.multiUpload.attachError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleExistingRoom = async (roomId: string) => {
    setBusyId(roomId);
    try {
      await onUploadFiles(files, roomId);
      onOpenChange(false);
      navigate(`/room/${roomId}`);
    } catch (e) {
      console.error(e);
      toast.error(t("dashboard.multiUpload.attachError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleKeepSeparate = async () => {
    setBusyId("__separate__");
    try {
      await onUploadFiles(files, null);
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error(t("dashboard.multiUpload.attachError"));
    } finally {
      setBusyId(null);
    }
  };

  // Dismiss (X / overlay / Esc): silently cancel — no upload, no DB writes.
  const handleDismiss = (next: boolean) => {
    if (busyId !== null) return; // ignore dismiss while uploading
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-md sm:rounded-3xl">
        <DialogHeader className="px-4 pt-4 pb-2 space-y-1">
          <DialogTitle className="text-lg font-normal">
            {t("dashboard.multiUpload.title")}
          </DialogTitle>
          <DialogDescription className="text-sm font-normal text-muted-foreground">
            {t("dashboard.multiUpload.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="p-1 max-h-[240px] overflow-y-auto scrollbar-hide">
          <button
            type="button"
            onClick={handleNewRoom}
            disabled={busyId !== null}
            className={cn(
              "group w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left",
              "hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50",
            )}
          >
            {busyId === "__new__" ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary/70" />
            ) : (
              <Plus className="h-4 w-4 text-primary/70 group-hover:text-primary" />
            )}
            <span className="text-sm font-medium text-primary/70 group-hover:text-primary">
              {t("dashboard.multiUpload.newRoom")}
            </span>
          </button>

          {rooms.map((room) => (
            <button
              key={room.id}
              type="button"
              onClick={() => handleExistingRoom(room.id)}
              disabled={busyId !== null}
              className={cn(
                "group w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left",
                "hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50",
              )}
            >
              {busyId === room.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Box className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
              )}
              <span className="truncate text-sm text-foreground">
                {room.name || t("dashboard.multiUpload.untitledRoom")}
              </span>
            </button>
          ))}
        </div>

        <div className="border-t border-border py-3 text-center">
          <button
            type="button"
            onClick={handleKeepSeparate}
            disabled={busyId !== null}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busyId === "__separate__" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("dashboard.multiUpload.keepSeparate")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
